import React from 'react';
import { cn } from '@/src/lib/utils';
import { isAdEnabledForCurrentPage } from '@/src/utils/adminConfig';

interface AdSlotProps {
  type?: 'TOP_AD' | 'MID_CONTENT_AD' | 'SIDE_AD' | 'AFFILIATE_SLOT';
  id?: 'TOP_AD' | 'MID_CONTENT_AD' | 'SIDE_AD' | 'AFFILIATE_SLOT';
  className?: string;
}

export default function AdSlot({ type, id, className }: AdSlotProps) {
  const [enabled, setEnabled] = React.useState(isAdEnabledForCurrentPage());

  React.useEffect(() => {
    const handler = () => setEnabled(isAdEnabledForCurrentPage());
    window.addEventListener('utilix-ads-config-changed', handler);
    window.addEventListener('popstate', handler);
    return () => {
      window.removeEventListener('utilix-ads-config-changed', handler);
      window.removeEventListener('popstate', handler);
    };
  }, []);

  const finalType = type || id || 'TOP_AD';
  
  if (!enabled) return null;
  
  const configs = {
    TOP_AD: {
      label: 'Advertisement',
      dimensions: {
        desktop: '728x90',
        tablet: '468x60',
        mobile: '320x50'
      },
      containerClasses: 'w-full max-w-[728px] mx-auto bg-slate-50 dark:bg-[#1e1f20] border-2 border-dashed border-slate-200 dark:border-slate-800 flex items-center justify-center rounded-2xl overflow-hidden',
      clsClasses: 'min-h-[50px] md:min-h-[60px] lg:min-h-[90px]',
    },
    MID_CONTENT_AD: {
      label: 'Advertisement',
      dimensions: {
        desktop: '728x90',
        mobile: '300x250'
      },
      containerClasses: 'w-full max-w-[728px] mx-auto bg-slate-50 dark:bg-[#1e1f20] border-2 border-dashed border-slate-200 dark:border-slate-800 flex items-center justify-center rounded-2xl overflow-hidden',
      clsClasses: 'min-h-[250px] lg:min-h-[90px]',
    },
    SIDE_AD: {
      label: 'Advertisement',
      dimensions: {
        desktop: '300x250'
      },
      containerClasses: 'w-full max-w-[300px] mx-auto bg-slate-50 dark:bg-[#1e1f20] border-2 border-dashed border-slate-200 dark:border-slate-800 flex items-center justify-center rounded-2xl overflow-hidden',
      clsClasses: 'min-h-[250px]',
    },
    AFFILIATE_SLOT: {
      label: 'Recommended Resource',
      dimensions: { desktop: 'Affiliate' },
      containerClasses: 'w-full bg-slate-900 border-2 border-slate-800 rounded-[2rem] p-8 flex flex-col items-center justify-center text-center overflow-hidden relative shadow-2xl',
      clsClasses: 'min-h-[200px]',
    },
  };

  const config = configs[finalType as keyof typeof configs] || configs.TOP_AD;

  return (
    <div 
      className={cn(
        'relative group transition-all duration-300 isolate no-print',
        config.containerClasses,
        config.clsClasses,
        className
      )}
      style={{ contentVisibility: 'auto' } as any}
    >
      <span className="absolute top-3 left-4 text-[9px] uppercase tracking-[0.2em] text-slate-400 font-black z-10">
        {config.label}
      </span>
      
      {finalType === 'AFFILIATE_SLOT' ? (
        <div className="relative z-10 space-y-4">
          <p className="text-white font-black text-2xl tracking-tighter max-w-sm">Elevate Your Wellness Journey with Premium Clinical Insights.</p>
          <button className="bg-brand text-white px-8 py-3 rounded-xl font-black text-xs uppercase tracking-widest hover:bg-brand-dark transition-all shadow-lg shadow-emerald-500/20 active:scale-95">
            Compare Premium Plans
          </button>
          <div className="absolute -bottom-12 -right-12 w-48 h-48 bg-brand/20 rounded-full blur-[80px] -z-10" />
        </div>
      ) : (
        <div className="flex flex-col items-center">
          <div className="hidden lg:block text-slate-300 font-mono font-black text-[10px] uppercase tracking-[0.5em]">
            {config.dimensions.desktop}
          </div>
          <div className="hidden md:block lg:hidden text-slate-300 font-mono font-black text-[10px] uppercase tracking-[0.5em]">
            {(config.dimensions as any).tablet || config.dimensions.desktop}
          </div>
          <div className="md:hidden text-slate-300 font-mono font-black text-[10px] uppercase tracking-[0.5em]">
            {(config.dimensions as any).mobile || config.dimensions.desktop}
          </div>
        </div>
      )}
    </div>
  );
}
